import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import backendClient from '../services/api/backendClient';
import { IUser } from '@interfaces/user';
import axios from 'axios';
import { toast } from 'react-toastify';

interface AuthContextType {
  user: IUser | null;
  setUser: React.Dispatch<React.SetStateAction<IUser | null>>;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const isAuthenticated = !!user;

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const response = await backendClient.get<IUser>('/auth/user');

        setUser(response.data);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          toast.error(
            'Authentication error: ' + error.response?.data?.message ||
              error.message ||
              'connection refused'
          );
        } else {
          toast.error('Unexpected Authentication error');
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
